import { FormEvent, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { createReservationRequest } from "../services/reservationRequest";
import { RESTAURANT_ID, RECAPTCHA_SITE_KEY } from "../config/restaurantConfig";
import { extractApiErrorMessage } from "../utils/apiError";
import OtpVerification from "./OtpVerification";
import DatePicker from "./DatePicker";

type Step = "form" | "otp" | "success";

const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) => String(10 + i).padStart(2, "0")); // 10..21
const MINUTE_OPTIONS = ["00", "15", "30", "45"];

export default function ReservationForm() {
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [date, setDate] = useState("");
  const [hour, setHour] = useState("");
  const [minute, setMinute] = useState("");
  const [guestCount, setGuestCount] = useState(2);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const [step, setStep] = useState<Step>("form");
  const [reservationId, setReservationId] = useState("");
  const [codeLifetimeMinutes, setCodeLifetimeMinutes] = useState(5);

  const allFieldsFilled = Boolean(fullName.trim() && phone.trim() && date && hour && minute);

  const handleSubmit = async (evt: FormEvent) => {
    evt.preventDefault();
    if (!allFieldsFilled) {
      setError("Lütfen ad soyad, telefon, tarih ve saat alanlarını doldurun.");
      return;
    }
    if (!captchaToken) {
      setError("Lütfen robot olmadığınızı doğrulayın.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const startsAtUtc = new Date(`${date}T${hour}:${minute}:00`).toISOString();
      const res = await createReservationRequest({
        restourantId: RESTAURANT_ID,
        startsAtUtc,
        partySize: guestCount,
        contactName: fullName.trim(),
        contactPhone: phone.trim(),
        note: note.trim() || undefined,
        captchaToken,
      });
      setReservationId(res.data.reservationId);
      setCodeLifetimeMinutes(res.data.codeLifetimeMinutes ?? 5);
      setStep("otp");
    } catch (err) {
      setError(
        extractApiErrorMessage(err, "Rezervasyon talebi gönderilirken bir sorun oluştu. Lütfen tekrar deneyin.")
      );
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    } finally {
      setSubmitting(false);
    }
  };

  if (step === "otp") {
    return (
      <section id="rezervasyon" className="rr-reservation">
        <div className="rr-section-title">
          <span>Bize Katılın</span>
          <h2>Rezervasyon</h2>
        </div>
        <OtpVerification
          reservationId={reservationId}
          codeLifetimeMinutes={codeLifetimeMinutes}
          contactPhone={phone}
          onVerified={() => setStep("success")}
          onCancel={() => setStep("form")}
        />
      </section>
    );
  }

  if (step === "success") {
    return (
      <section id="rezervasyon" className="rr-reservation">
        <div className="rr-reservation-success">
          <i className="bi bi-check-circle-fill" style={{ fontSize: "2.4rem", color: "#b8934a" }}></i>
          <h3 style={{ marginTop: 14, fontWeight: 800 }}>Rezervasyon İsteğiniz Restorana Gönderildi</h3>
          <p style={{ color: "#6b6459", marginTop: 8 }}>
            En kısa sürede bilgilendirileceksiniz.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="rezervasyon" className="rr-reservation">
      <div className="rr-section-title">
        <span>Bize Katılın</span>
        <h2>Rezervasyon</h2>
      </div>

      <form className="rr-reservation-form" onSubmit={handleSubmit}>
        {error && (
          <div className="rr-form-group full">
            <div className="rr-form-alert">
              <i className="bi bi-exclamation-circle-fill"></i>
              <span>{error}</span>
            </div>
          </div>
        )}

        <div className="rr-form-group">
          <label>Ad Soyad *</label>
          <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Adınız Soyadınız" />
        </div>

        <div className="rr-form-group">
          <label>Telefon *</label>
          <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="05XX XXX XX XX" />
        </div>

        <div className="rr-form-group">
          <label>Kişi Sayısı</label>
          <input
            type="number"
            min={1}
            max={20}
            value={guestCount}
            onChange={(e) => setGuestCount(Number(e.target.value))}
          />
        </div>

        <div className="rr-form-group">
          <label>Tarih *</label>
          <DatePicker value={date} onChange={setDate} />
        </div>

        <div className="rr-form-group">
          <label>Saat *</label>
          <div className="rr-time-select-group">
            <select value={hour} onChange={(e) => setHour(e.target.value)}>
              <option value="" disabled>Saat</option>
              {HOUR_OPTIONS.map((h) => (
                <option key={h} value={h}>{h}</option>
              ))}
            </select>
            <span className="rr-time-select-sep">:</span>
            <select value={minute} onChange={(e) => setMinute(e.target.value)}>
              <option value="" disabled>Dakika</option>
              {MINUTE_OPTIONS.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="rr-form-group full">
          <label>Not</label>
          <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Özel bir isteğiniz varsa belirtebilirsiniz" />
        </div>

        {allFieldsFilled && (
          <div className="rr-form-group full rr-captcha-group">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={RECAPTCHA_SITE_KEY}
              hl="tr"
              onChange={(token) => setCaptchaToken(token)}
              onExpired={() => setCaptchaToken(null)}
              onErrored={() => setCaptchaToken(null)}
            />
          </div>
        )}

        <div className="rr-form-group full">
          <button
            type="submit"
            className="rr-btn-gold"
            style={{ width: "100%", justifyContent: "center" }}
            disabled={submitting || !captchaToken}
          >
            {submitting ? (
              <span className="spinner-border spinner-border-sm" role="status" />
            ) : (
              <i className="bi bi-send-fill"></i>
            )}
            {submitting ? "Gönderiliyor..." : "Rezervasyon Talebi Gönder"}
          </button>
        </div>
      </form>
    </section>
  );
}
